import { RowClickedEvent } from '@ag-grid-community/core'
import { DataTable, DataTablePagination } from 'components/common'
import { useQuery } from 'hooks'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { SetSelectedColumns } from 'types/dataTable'
import { fetchPricings } from 'utils/api/pricingApi'
import { PAGE_PRICING_UPDATE } from 'utils/constants'
import { columns } from './utils'
import toast from 'react-hot-toast'
import {
  BARCODE_COUNT_KEY,
  BARCODE_ERROR_MESSAGES,
  BARCODE_QUERY_KEY,
  BARCODE_SEARCH_EXPIRED,
  BARCODE_TOKEN_KEY,
  getBarcodeErrorCode,
} from 'components/common/barcodeSearch/constants'

interface PricingDataTableProps {
  isEditor: boolean
  setSelectedColumns: SetSelectedColumns
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

const PricingDataTable = ({
  isEditor,
  setSelectedColumns,
  loading,
  setLoading,
}: PricingDataTableProps) => {
  const [pricingResponse, setPricingResponse] = useState<any>({})

  const navigate = useNavigate()
  const { search } = useLocation()
  const { getAllQueries, setQuery, setMultipleQueries } = useQuery()

  // To fetch pricing Data
  const getPricingData = async () => {
    setLoading(true)
    const queries = getAllQueries()
    const { limit, page, search, pricingFilters, barcodeToken } = queries
    const barcode = queries[BARCODE_QUERY_KEY]
    try {
      const { data } = await fetchPricings({
        limit: limit || 10,
        page: page || 1,
        search: search,
        filters: pricingFilters,
        barcodeToken: barcodeToken,
        barcode: barcode,
      })

      setSelectedColumns(data?.tableConfig?.pricing || [])
      setPricingResponse({ ...data })
    } catch (error) {
      // Barcode search token expired → clear it (triggers a normal refetch)
      // and prompt the user to upload the file again.
      if (getBarcodeErrorCode(error) === BARCODE_SEARCH_EXPIRED) {
        setMultipleQueries({
          [BARCODE_TOKEN_KEY]: undefined,
          [BARCODE_COUNT_KEY]: undefined,
          page: 1,
        })
        toast.error(BARCODE_ERROR_MESSAGES[BARCODE_SEARCH_EXPIRED], {
          duration: 4000,
        })
      }
      setPricingResponse({ docs: [] })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getPricingData()
  }, [search])

  useEffect(() => {
    const columnUrlParams = localStorage.getItem('pricingColumnSelected')
    if (columnUrlParams) {
      setQuery('cols', JSON.parse(columnUrlParams))
    }
  }, [])

  // Will redirect to edit form
  const redirectToEdit = (e: RowClickedEvent) => {
    if (!isEditor) return
    navigate(`${PAGE_PRICING_UPDATE.path}/${e.data._id}`)
  }

  // Pricing list totals are NOT filter-aware (totalDocs/totalPages count the
  // whole collection), so during a barcode search the backend's
  // hasNextPage/totalPages can't be trusted. Drive paging from the page
  // contents instead: a full page implies there may be another one.
  const activeQueries = getAllQueries()
  const barcodeActive = !!(
    activeQueries[BARCODE_TOKEN_KEY] || activeQueries[BARCODE_QUERY_KEY]
  )
  const rows = pricingResponse?.docs ?? []
  const currentPage = Number(pricingResponse?.page ?? 1)
  const pageLimit = Number(pricingResponse?.limit ?? 10)

  const hasNextPage = barcodeActive
    ? rows.length >= pageLimit
    : pricingResponse?.hasNextPage
  const hasPrevPage = barcodeActive
    ? currentPage > 1
    : pricingResponse?.hasPrevPage
  const nextPage = barcodeActive ? currentPage + 1 : pricingResponse?.nextPage
  const prevPage = barcodeActive ? currentPage - 1 : pricingResponse?.prevPage
  const totalPages = barcodeActive
    ? currentPage + (hasNextPage ? 1 : 0)
    : pricingResponse?.totalPages

  return (
    <>
      {/* Table */}

      <DataTable
        columns={loading ? [] : columns}
        rows={loading ? null : pricingResponse.docs}
        identifier="_id"
        onRowClicked={isEditor ? redirectToEdit : () => {}}
        activeColumns={pricingResponse?.tableConfig?.pricing || []}
        activeColumnsKey="pricing"
      />

      {/* Table Pagination */}
      <DataTablePagination
        currentPage={pricingResponse?.page}
        totalPages={totalPages}
        hasNextPage={hasNextPage}
        hasPrevPage={hasPrevPage}
        limit={pricingResponse?.limit}
        nextPage={nextPage}
        prevPage={prevPage}
        disabled={loading}
        shouldDisplayTotalPages={false}
        disableGoToLastPage
      />
    </>
  )
}

export default PricingDataTable
